// ─────────────────────────────────────────────────────────────────────────────────────────────────
// GradePass — the battle's single post-processing pass.
//
// ONE PASS, N EFFECTS. Every fullscreen pass costs a full framebuffer read AND write (~8MB each way at
// 1080p), so a chain of five passes burns ~80MB/frame of bandwidth before shading anything. Folded into one
// shader that is ~16MB. Everything here that does not need a blur therefore lives in a single draw: sharpen,
// LUT grade, depth outline, CRT, chromatic aberration, vignette, grain. Bloom keeps its own passes because it
// genuinely needs a blur, and it runs at half res where nobody can tell.
//
// Disabled features are COMPILED OUT, not branched around: the fragment source is assembled from the enabled
// set and the material is rebuilt when that set changes (rare — a settings toggle). An unused effect costs
// zero instructions and zero texture reads, rather than a uniform branch and a sampler that still occupies a
// register. Toggling rebuilds one shader; leaving it alone costs nothing.
//
// RENDER SCALE IS THE TRAP. Dynamic resolution drives the canvas backing store as low as 0.5 (PERF.md caught
// it at 353x256). Anything with a PITCH — grain, scanlines, the CRT aperture mask — would silently change
// character mid-battle as the scaler moved, going from a fine phosphor grille to chunky stripes exactly when
// the frame is already struggling. So pitch-based effects are sized in SCREEN space via `uPitch`, which
// divides out the render scale; the mask stays put while the resolution moves underneath it.
// ─────────────────────────────────────────────────────────────────────────────────────────────────
import * as THREE from "three";

export interface GradeOpts {
  sharpen: number;   // CAS strength, 0..1. The highest-value effect here BECAUSE of dynamic resolution:
                     // it recovers most of what a 0.5 render scale takes away, for four texture taps.
  lut: boolean;      // 3D colour grade via a tiled 2D strip (two taps replace ALL curve math)
  crt: boolean;      // barrel distortion + aperture grille + beam-profile scanlines
  outline: number;   // depth-discontinuity edge darkening, 0 = off. READABILITY first, style second.
  chroma: number;    // radial RGB split
  vignette: number;
  grain: number;
}

export const GRADE_DEFAULTS: GradeOpts = {
  sharpen: 0.55, lut: true, crt: true, outline: 0.35, chroma: 0.0022, vignette: 0.42, grain: 0.035,
};

const VERT = "varying vec2 vUv; void main(){ vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }";

/** LUT_N^3 colour cube, laid out as a horizontal strip of LUT_N slices (LUT_N*LUT_N x LUT_N). A 2D strip
 *  rather than a real 3D texture on purpose: sampler3D forces the whole shader to GLSL3, and two taps plus a
 *  mix is cheap enough that the portability is worth more than the third dimension. */
const LUT_N = 32;

/** Build a colour cube from ordinary grading controls. The POINT of a LUT is that however expensive the maths
 *  here, the shader pays two texture reads — so this can be as elaborate as it likes. Runs once per look. */
export function makeLut(o: {
  lift?: [number, number, number]; gain?: [number, number, number];
  gamma?: number; sat?: number; temp?: number; contrast?: number;
} = {}): THREE.DataTexture {
  const lift = o.lift ?? [0, 0, 0], gain = o.gain ?? [1, 1, 1];
  const gamma = o.gamma ?? 1, sat = o.sat ?? 1, temp = o.temp ?? 0, con = o.contrast ?? 1;
  const w = LUT_N * LUT_N, h = LUT_N;
  const data = new Uint8Array(w * h * 4);
  for (let b = 0; b < LUT_N; b++) {
    for (let g = 0; g < LUT_N; g++) {
      for (let r = 0; r < LUT_N; r++) {
        let c = [r / (LUT_N - 1), g / (LUT_N - 1), b / (LUT_N - 1)];
        c = c.map((v, i) => Math.pow(Math.max(0, v * gain[i] + lift[i]), 1 / gamma));
        c[0] += temp * 0.06; c[2] -= temp * 0.06;                       // warm/cool on the ends only
        const l = c[0] * 0.2126 + c[1] * 0.7152 + c[2] * 0.0722;
        c = c.map((v) => l + (v - l) * sat);
        c = c.map((v) => (v - 0.5) * con + 0.5);
        const x = b * LUT_N + r, i = (g * w + x) * 4;
        data[i] = Math.max(0, Math.min(255, c[0] * 255));
        data[i + 1] = Math.max(0, Math.min(255, c[1] * 255));
        data[i + 2] = Math.max(0, Math.min(255, c[2] * 255));
        data[i + 3] = 255;
      }
    }
  }
  const t = new THREE.DataTexture(data, w, h, THREE.RGBAFormat);
  t.minFilter = t.magFilter = THREE.LinearFilter;
  t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
  t.generateMipmaps = false;
  t.needsUpdate = true;
  return t;
}

/** A few ready looks, so the mechanism is useful before anyone authors a .cube. */
export const LUTS = {
  neutral: (): THREE.DataTexture => makeLut(),
  // cold vacuum-lit steel — the default battle look
  steel: (): THREE.DataTexture => makeLut({ temp: -0.35, sat: 0.9, contrast: 1.08, lift: [0.004, 0.008, 0.02] }),
  // hot dusty world
  ember: (): THREE.DataTexture => makeLut({ temp: 0.4, sat: 1.05, contrast: 1.05, lift: [0.02, 0.006, 0] }),
  // sensor feed: crushed, desaturated, green-lifted
  probe: (): THREE.DataTexture => makeLut({ sat: 0.55, contrast: 1.18, lift: [0, 0.03, 0.008], gamma: 0.94 }),
};

export class GradePass {
  enabled = true;
  opts: GradeOpts = { ...GRADE_DEFAULTS };
  target!: THREE.WebGLRenderTarget;
  private mat: THREE.ShaderMaterial | null = null;
  private scene = new THREE.Scene();
  private cam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private quad: THREE.Mesh;
  private lutTex: THREE.DataTexture | null = null;
  private key = "";
  private near = 0.1; private far = 1;
  // CRT sub-dials. Separate from GradeOpts because they only mean anything while `crt` is on, and they are
  // pure uniforms — changing them never rebuilds the shader.
  private crtAmt = 1; private maskPitch = 3; private curvature = 0.18;

  constructor(private renderer: THREE.WebGLRenderer) {
    const ds = renderer.getDrawingBufferSize(new THREE.Vector2());
    const depth = new THREE.DepthTexture(Math.max(1, ds.x), Math.max(1, ds.y));
    depth.type = THREE.UnsignedIntType;
    this.target = new THREE.WebGLRenderTarget(Math.max(1, ds.x), Math.max(1, ds.y), {
      depthBuffer: true, stencilBuffer: false, depthTexture: depth,
      minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter,
    });
    // COLOUR SPACE. three encodes linear->sRGB on the way to the CANVAS, but not into a render target, so a
    // naive RT swap hands the post pass linear values it then writes out raw — measured as a 64% darkening at
    // screen centre, where vignette provably does nothing. Declaring the target sRGB puts the encode back
    // where it was, and has the happy side effect that the LUT and the sharpen both operate in display space,
    // which is the convention they are authored for.
    this.target.texture.colorSpace = THREE.SRGBColorSpace;
    this.quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2));
    this.quad.frustumCulled = false;
    this.scene.add(this.quad);
    this.setLut(LUTS.steel());
    this.rebuild();
  }

  setLut(t: THREE.DataTexture): void {
    this.lutTex?.dispose();
    this.lutTex = t;
    if (this.mat) this.mat.uniforms.uLut.value = t;
  }

  setSize(w: number, h: number): void {
    this.target.setSize(Math.max(1, w), Math.max(1, h));
  }

  /** Feature set → shader source. Rebuilt only when the set changes, so runtime cost is a string compare. */
  private rebuild(): void {
    const o = this.opts;
    const k = `${o.sharpen > 0}|${o.lut}|${o.crt}|${o.outline > 0}|${o.chroma > 0}|${o.vignette > 0}|${o.grain > 0}`;
    if (k === this.key && this.mat) return;
    this.key = k;

    const f: string[] = [
      "precision highp float;",
      "varying vec2 vUv;",
      "uniform sampler2D uTex; uniform vec2 uTexel; uniform float uTime; uniform vec2 uPitch;",
    ];
    if (o.lut) f.push("uniform sampler2D uLut;");
    if (o.outline > 0) f.push("uniform sampler2D uDepth; uniform float uNear, uFar, uOutline;");
    if (o.sharpen > 0) f.push("uniform float uSharpen;");
    if (o.chroma > 0) f.push("uniform float uChroma;");
    if (o.vignette > 0) f.push("uniform float uVignette;");
    if (o.grain > 0) f.push("uniform float uGrain;");
    if (o.crt) f.push("uniform float uCrt, uMask, uCurve;");
    f.push("float hash(vec2 p){ return fract(sin(dot(p, vec2(41.3, 289.1))) * 43758.5453); }");
    if (o.outline > 0) {
      f.push("float lin(float d){ float z = d*2.0-1.0; return (2.0*uNear*uFar)/(uFar+uNear-z*(uFar-uNear)); }");
    }
    f.push("void main(){");
    f.push("  vec2 uv = vUv;");

    if (o.crt) {
      // Barrel distortion FIRST — it is the single strongest "this is a screen" cue, more than scanlines, and
      // it must warp the sampling UV before anything reads the texture.
      f.push("  vec2 cc = uv - 0.5; float r2 = dot(cc, cc);");
      f.push("  uv = 0.5 + cc * (1.0 + uCurve * uCrt * r2);");
      f.push("  if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) { gl_FragColor = vec4(0.0,0.0,0.0,1.0); return; }");
    }

    if (o.sharpen > 0) {
      // AMD CAS in shape: a cross of four taps, with the sharpening amount driven by local contrast so flat
      // areas (sky, fog) stay clean and only real detail is lifted. This is the effect that pays for dynamic
      // resolution — at renderScale 0.5 it is the difference between soft and crisp.
      f.push("  vec3 c = texture2D(uTex, uv).rgb;");
      f.push("  vec3 nn = texture2D(uTex, uv + vec2(0.0, -uTexel.y)).rgb;");
      f.push("  vec3 ss = texture2D(uTex, uv + vec2(0.0,  uTexel.y)).rgb;");
      f.push("  vec3 ww = texture2D(uTex, uv + vec2(-uTexel.x, 0.0)).rgb;");
      f.push("  vec3 ee = texture2D(uTex, uv + vec2( uTexel.x, 0.0)).rgb;");
      f.push("  vec3 mn = min(c, min(min(nn, ss), min(ww, ee)));");
      f.push("  vec3 mx = max(c, max(max(nn, ss), max(ww, ee)));");
      f.push("  vec3 amp = clamp(min(mn, 1.0 - mx) / max(mx, 1e-4), 0.0, 1.0);");
      f.push("  amp = sqrt(amp);");
      f.push("  float wgt = -0.2 * uSharpen * (amp.r + amp.g + amp.b) / 3.0;");
      f.push("  vec3 col = (c + (nn + ss + ww + ee) * wgt) / (1.0 + 4.0 * wgt);");
    } else {
      f.push("  vec3 col = texture2D(uTex, uv).rgb;");
    }

    if (o.chroma > 0) {
      // Radial split, stronger toward the edges. Two extra taps; reuses the already-warped uv.
      f.push("  vec2 ch = (uv - 0.5) * uChroma * (dot(uv - 0.5, uv - 0.5) * 4.0);");
      f.push("  col.r = texture2D(uTex, uv + ch).r; col.b = texture2D(uTex, uv - ch).b;");
    }

    if (o.outline > 0) {
      // Depth discontinuity → a dark edge. Costs four taps of a depth buffer the scene pass already wrote, so
      // it is nearly free, and it earns its place on READABILITY: units and machines separate from the terrain
      // behind them in a busy factory view. Threshold scales with distance or everything far away outlines.
      f.push("  float d0 = lin(texture2D(uDepth, uv).x);");
      f.push("  float dl = lin(texture2D(uDepth, uv + vec2(-uTexel.x, 0.0)).x);");
      f.push("  float dr = lin(texture2D(uDepth, uv + vec2( uTexel.x, 0.0)).x);");
      f.push("  float du = lin(texture2D(uDepth, uv + vec2(0.0, -uTexel.y)).x);");
      f.push("  float dd = lin(texture2D(uDepth, uv + vec2(0.0,  uTexel.y)).x);");
      f.push("  float g = max(max(abs(d0-dl), abs(d0-dr)), max(abs(d0-du), abs(d0-dd)));");
      f.push("  float edge = smoothstep(0.012 * d0, 0.05 * d0, g);");
      f.push("  col *= 1.0 - edge * uOutline;");
    }

    if (o.lut) {
      // Two taps + a mix across the blue axis. Whatever the look cost to author, the frame pays this.
      f.push(`  float lb = clamp(col.b, 0.0, 1.0) * ${(LUT_N - 1).toFixed(1)};`);
      f.push("  float lb0 = floor(lb), lbf = lb - lb0;");
      f.push(`  vec2 lrg = clamp(col.rg, 0.0, 1.0) * ${((LUT_N - 1) / LUT_N).toFixed(6)} + ${(0.5 / LUT_N).toFixed(6)};`);
      f.push(`  vec2 la = vec2((lb0 + lrg.r) / ${LUT_N}.0, lrg.g);`);
      f.push(`  vec2 lc = vec2((min(lb0 + 1.0, ${(LUT_N - 1).toFixed(1)}) + lrg.r) / ${LUT_N}.0, lrg.g);`);
      f.push("  col = mix(texture2D(uLut, la).rgb, texture2D(uLut, lc).rgb, lbf);");
    }

    if (o.crt) {
      // APERTURE GRILLE + BEAM. The mask is what makes this read as a CRT rather than as lines drawn over a
      // picture, and it is free: a function of gl_FragCoord, no texture reads. uPitch carries the render-scale
      // compensation so the triads stay the same size on the player's screen whatever the scaler is doing.
      f.push("  vec2 px = gl_FragCoord.xy * uPitch;");
      f.push("  float ph = mod(px.x, uMask);");
      f.push("  float t3 = uMask / 3.0;");
      f.push("  vec3 mask = vec3(ph < t3 ? 1.0 : 0.55, (ph >= t3 && ph < t3*2.0) ? 1.0 : 0.55, ph >= t3*2.0 ? 1.0 : 0.55);");
      // Gaussian beam rather than a sine: the bright centre with soft falloff is where a CRT's glow comes from.
      f.push("  float sy = fract(px.y * 0.5); float beam = exp(-pow((sy - 0.5) * 2.6, 2.0));");
      f.push("  col *= mix(vec3(1.0), mask * (0.72 + 0.55 * beam), uCrt);");
      f.push("  col *= mix(1.0, 1.0 - 0.35 * r2, uCrt);");   // tube corner falloff, reusing r2 from the warp
    }

    if (o.vignette > 0) {
      // vr spans 0 (centre) .. 0.5 (corner). The ported smoothstep(1.1, 0.35, vr*1.6) stayed pinned at 1.0
      // until vr > 0.22, i.e. it only touched the extreme corners — measured 0.02 MAD, an effect that was not
      // an effect. A power falloff darkens smoothly all the way out and actually reaches uVignette at the corner.
      f.push("  vec2 vc = vUv - 0.5; float vr = clamp(dot(vc, vc) * 2.0, 0.0, 1.0);");
      f.push("  col *= 1.0 - uVignette * pow(vr, 1.5);");
    }
    if (o.grain > 0) {
      // Grain sized in SCREEN pixels (uPitch) so the scaler cannot turn fine grain into coarse blotches.
      f.push("  col += (hash(gl_FragCoord.xy * uPitch + fract(uTime) * 91.7) - 0.5) * uGrain;");
    }
    f.push("  gl_FragColor = vec4(col, 1.0);");
    f.push("}");

    this.mat?.dispose();
    this.mat = new THREE.ShaderMaterial({
      uniforms: {
        uTex: { value: null }, uDepth: { value: null }, uLut: { value: this.lutTex },
        uTexel: { value: new THREE.Vector2() }, uPitch: { value: new THREE.Vector2(1, 1) },
        uTime: { value: 0 }, uNear: { value: 0.1 }, uFar: { value: 1000 },
        uSharpen: { value: o.sharpen }, uChroma: { value: o.chroma }, uVignette: { value: o.vignette },
        uGrain: { value: o.grain }, uOutline: { value: o.outline }, uCrt: { value: o.crt ? this.crtAmt : 0 },
        uMask: { value: this.maskPitch }, uCurve: { value: this.curvature },
      },
      vertexShader: VERT,
      fragmentShader: f.join("\n"),
      depthTest: false, depthWrite: false,
    });
    this.quad.material = this.mat;
  }

  /** Push live option values; rebuilds the shader only if a feature turned on or off. */
  set(opts: Partial<GradeOpts>): void {
    Object.assign(this.opts, opts);
    this.rebuild();
    const u = this.mat!.uniforms, o = this.opts;
    u.uSharpen.value = o.sharpen; u.uChroma.value = o.chroma; u.uVignette.value = o.vignette;
    u.uGrain.value = o.grain; u.uOutline.value = o.outline; u.uCrt.value = o.crt ? this.crtAmt : 0;
    if (u.uMask) u.uMask.value = this.maskPitch;
    if (u.uCurve) u.uCurve.value = this.curvature;
  }

  setCamera(near: number, far: number): void { this.near = near; this.far = far; }

  setCrtAmount(v: number): void { this.crtAmt = v; if (this.mat) this.mat.uniforms.uCrt.value = this.opts.crt ? v : 0; }
  /** Mask cell width in SCREEN pixels. 3 = one pixel per phosphor; higher = a coarser, more visible grille. */
  setMaskPitch(v: number): void { this.maskPitch = v; if (this.mat) this.mat.uniforms.uMask.value = v; }
  setCurvature(v: number): void { this.curvature = v; if (this.mat) this.mat.uniforms.uCurve.value = v; }

  /** The current state as a paste-able source block — tune by eye, then hand the numbers back to the file. */
  snapshotDefaults(): string {
    const o = this.opts, n = (v: number, d = 3): string => Number(v.toFixed(d)).toString();
    return `export const GRADE_DEFAULTS: GradeOpts = {
` +
      `  sharpen: ${n(o.sharpen)}, lut: ${o.lut}, crt: ${o.crt}, outline: ${n(o.outline)},
` +
      `  chroma: ${n(o.chroma, 4)}, vignette: ${n(o.vignette)}, grain: ${n(o.grain)},
};
` +
      `// CRT sub-dials (grade.ts fields): crtAmt ${n(this.crtAmt)}, maskPitch ${n(this.maskPitch, 1)}, ` +
      `curvature ${n(this.curvature)}`;
  }

  /** RT -> screen. One draw, every enabled effect.
   *  `out` exists so the result can be READ BACK: the default framebuffer cannot be sampled reliably with
   *  preserveDrawingBuffer:false, so every attempt to verify this pass by reading the canvas returns stale
   *  bytes and quietly reports that nothing changed. Render into a target instead and the output is real. */
  run(t: number, renderScale: number, out: THREE.WebGLRenderTarget | null = null): void {
    if (!this.mat) return;
    const u = this.mat.uniforms;
    u.uTex.value = this.target.texture;
    u.uDepth.value = this.target.depthTexture;
    u.uTime.value = t;
    u.uNear.value = this.near; u.uFar.value = this.far;
    (u.uTexel.value as THREE.Vector2).set(1 / this.target.width, 1 / this.target.height);
    // Pitch divides OUT the render scale, so a mask/grain cell keeps its size on the display even as the
    // backing store shrinks under dynamic resolution.
    const p = renderScale > 0 ? 1 / renderScale : 1;
    (u.uPitch.value as THREE.Vector2).set(p, p);
    const prev = this.renderer.getRenderTarget();
    this.renderer.setRenderTarget(out);
    const auto = this.renderer.autoClear;
    this.renderer.autoClear = false;
    this.renderer.render(this.scene, this.cam);
    this.renderer.autoClear = auto;
    if (prev) this.renderer.setRenderTarget(prev);
  }

  dispose(): void {
    this.target.dispose(); this.mat?.dispose(); this.lutTex?.dispose();
    (this.quad.geometry as THREE.BufferGeometry).dispose();
  }
}
